import type { EntrySuppressedVisits, PostHogConfig } from '../analytics-resolve';
import { maskAutocaptureContent } from './posthog-autocapture-mask';
import { createUrlTemplateStage } from './posthog-url-template';

/**
 * The posthog-js `before_send` hook: every privacy decision the web provider
 * makes about an event that is about to leave the browser.
 *
 * Relies on posthog-js routing every event through `capture()` and
 * `before_send` — session replay (`$snapshot`) and heatmaps (`$$heatmap`)
 * included — and on `stopSessionRecording()` flushing any buffered replay
 * through the same path (verified in 1.399.4). Re-verify on any posthog-js
 * version change: an SDK that bypassed `before_send` for replay or heatmaps
 * would reopen these leaks silently. The real-SDK tests in this package cover
 * heatmaps, autocapture and persisted attribution only: replay is tested at
 * the hook, so a change in how the SDK routes it would not fail them.
 */

/** The fields of a posthog-js capture result the hook reads and edits. */
export interface BeforeSendEvent {
  event: string;
  properties: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
  timestamp?: unknown;
}

export type BeforeSend = <T extends BeforeSendEvent>(event: T | null) => T | null;

/** The privacy options of `PostHogConfig` the hook acts on. */
export type PostHogPrivacyOptions = Pick<
  PostHogConfig,
  | 'suppressedPathPatterns'
  | 'heatmapExcludedPathPatterns'
  | 'heatmapFixedPointExcludedPathPatterns'
  | 'entrySuppressedVisits'
  | 'entryAttributionExcludedPathPatterns'
  | 'autocaptureExcludedPathPatterns'
  | 'pageGeometryExcludedPathPatterns'
  | 'previousPageviewExcluded'
  | 'replayPausedPathPatterns'
  | 'replayPausedWhileHeld'
  | 'maskAutocaptureContent'
  | 'urlPageTemplates'
>;

/** The SDK's replay switches; inert unless replay was explicitly enabled. */
export interface ReplayControls {
  stopSessionRecording(): void;
  startSessionRecording(): void;
}

export const INERT_REPLAY_CONTROLS: ReplayControls = Object.freeze({
  stopSessionRecording: () => {},
  startSessionRecording: () => {},
});

export interface BeforeSendRuntime {
  now?: () => number;
  /**
   * Runs a replay switch outside the hook. Calling posthog-js from inside its
   * own `before_send` would re-enter `capture`, so switches are deferred.
   */
  schedule?: (task: () => void) => void;
  /**
   * Runs the replay RESUME later still: long enough for the page being left
   * to have unmounted, so the fresh full snapshot a restart takes shows the
   * next page, not the paused one. Defaults to a timer of `RESUME_DELAY_MS`.
   */
  deferResume?: (task: () => void) => void;
}

/**
 * How long after leaving a paused page replay stays paused. A route change is
 * observed when the history entry changes, which can be before the app has
 * re-rendered (a browser Back/Forward notifies the SDK's listener first); the
 * paused interval stays open until the resume actually runs.
 */
export const RESUME_DELAY_MS = 500;

/**
 * Upper bound on values inspected per event. A replay batch can hold a whole
 * DOM snapshot; past this budget the walk stops and the answer is "matches",
 * so a payload too large to inspect is treated as one that cannot be cleared.
 */
const SUPPRESSION_WALK_BUDGET = 200_000;

/** Whether any string, key or value, at any depth of `value` matches. Fails closed over budget. */
function containsSuppressedPath(value: unknown, patterns: readonly Matcher[]): boolean {
  const stack: unknown[] = [value];
  let visited = 0;
  while (stack.length > 0) {
    visited += 1;
    if (visited > SUPPRESSION_WALK_BUDGET) return true;
    const current = stack.pop();
    if (typeof current === 'string') {
      if (patterns.some((pattern) => pattern.test(current))) return true;
    } else if (Array.isArray(current)) {
      for (const item of current) stack.push(item);
    } else if (isRecord(current)) {
      // Keys too, not only values: posthog-js keys some payloads by page URL
      // (heatmaps buffer as `$heatmap_data: { [href]: [...] }`).
      for (const [key, item] of Object.entries(current)) {
        stack.push(key);
        stack.push(item);
      }
    }
  }
  return false;
}

function deleteSuppressedKeys(
  record: Record<string, unknown> | undefined,
  patterns: readonly Matcher[],
): void {
  if (!record || patterns.length === 0) return;
  for (const [key, value] of Object.entries(record)) {
    if (containsSuppressedPath(value, patterns)) delete record[key];
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** What the stages match paths and URLs with. A `RegExp` is one. */
interface Matcher {
  test(value: string): boolean;
}

/**
 * The forms a path or URL can take that the router treats alike: as written,
 * after `decodeURI`, and after `decodeURIComponent` (each applied once). The router decodes before matching, so
 * `/%6dembers/<id>` renders the member page; a rule tested only against the
 * raw form would miss it. Matching any form fails closed.
 */
function decodedForms(value: string): string[] {
  const forms = [value];
  for (const decode of [decodeURI, decodeURIComponent]) {
    try {
      const decoded = decode(value);
      if (!forms.includes(decoded)) forms.push(decoded);
    } catch {
      // A malformed escape: the raw form is all there is.
    }
  }
  return forms;
}

/**
 * A global or sticky regex keeps `lastIndex` between `.test()` calls and would
 * answer differently for the same input; normalise them away. Every matcher
 * also tests the decoded forms of its input.
 */
function stable(patterns: readonly RegExp[] | undefined): Matcher[] {
  return (patterns ?? []).map((pattern) => {
    const regex = new RegExp(pattern.source, pattern.flags.replace(/[gy]/g, ''));
    return { test: (value: string) => decodedForms(value).some((form) => regex.test(form)) };
  });
}

/** The page's own URL when there is one; absent outside a browser. */
function currentPageHref(): string | undefined {
  const href: unknown = globalThis.location?.href;
  return typeof href === 'string' ? href : undefined;
}

/** The path of a URL, resolved against the page; undefined when it is not one. */
function pathnameOf(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  try {
    const base: unknown = globalThis.location?.origin;
    return new URL(value, typeof base === 'string' && base !== 'null' ? base : 'http://localhost')
      .pathname;
  } catch {
    return undefined;
  }
}

/** Where the event was captured: its own URL, which outlives a navigation. */
function originPath(properties: Record<string, unknown>): string | undefined {
  return (
    pathnameOf(properties.$current_url) ??
    (typeof properties.$pathname === 'string' ? properties.$pathname : undefined)
  );
}

/** The page the browser is on now; the event's own URL outside a browser. */
function livePath(properties: Record<string, unknown>): string | undefined {
  return pathnameOf(currentPageHref()) ?? originPath(properties);
}

// Epoch milliseconds throughout, not Luxon (ADR-096): these are the SDK's and
// rrweb's own timestamps, compared only with each other and with `Date.now()`;
// no calendar or zone arithmetic happens here.
function eventTime(event: BeforeSendEvent, now: number): number {
  const { timestamp } = event;
  if (timestamp instanceof Date && Number.isFinite(timestamp.getTime())) return timestamp.getTime();
  if (typeof timestamp === 'number' && Number.isFinite(timestamp)) return timestamp;
  return now;
}

function exactPathPattern(path: string): Matcher {
  // The path must end where the URL's path ends, so `/members/a` never
  // matches `/members/ab`.
  const regex = new RegExp(`${path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=[/?#"'\\s]|$)`);
  return { test: (value: string) => decodedForms(value).some((form) => regex.test(form)) };
}

// ─── Stage: suppressed paths (an escalation link, say) ────────────────────────

interface ReplayQuarantine {
  /** Once set, no replay leaves this page load again. A reload lifts it. */
  quarantined: boolean;
}

/**
 * Keeps suppressed paths out of PostHog entirely, in three moves:
 *
 * 1. An event captured while a suppressed path is the current page is dropped
 *    — the visit itself is what must not be recorded.
 * 2. Session replay (`$snapshot`) is QUARANTINED for the rest of the page's
 *    life from the first moment a suppressed path is seen — on an event's URL,
 *    inside a replay batch, or as the browser's current location when the hook
 *    runs. Matching batches by content is not enough: an incremental mutation
 *    batch carries no URL at all, yet it is the suppressed page's DOM, and the
 *    recorder buffers and compresses batches before this hook sees them.
 * 3. Any other event loses every top-level property, `$set` or `$set_once`
 *    entry that carries a suppressed path — previous-pageview, session-entry,
 *    initial-URL and referrer context all outlive the page itself.
 *
 * Returns true when the event is to be dropped.
 */
function suppressedPathStage(
  matchers: readonly Matcher[],
  replay: ReplayQuarantine,
): (event: BeforeSendEvent) => boolean {
  const matches = (value: unknown): boolean =>
    typeof value === 'string' && matchers.some((m) => m.test(value));
  return (event) => {
    if (matchers.length === 0) return false;
    const { properties } = event;
    const onSuppressedPage =
      matches(currentPageHref()) ||
      matches(properties.$current_url) ||
      matches(properties.$pathname);
    if (onSuppressedPage) replay.quarantined = true;
    if (event.event === '$snapshot') {
      if (!replay.quarantined && containsSuppressedPath(properties.$snapshot_data, matchers)) {
        replay.quarantined = true;
      }
      return replay.quarantined;
    }
    if (onSuppressedPage) return true;
    deleteSuppressedKeys(properties, matchers);
    deleteSuppressedKeys(event.$set, matchers);
    deleteSuppressedKeys(event.$set_once, matchers);
    return false;
  };
}

/**
 * The suppressed-path hook on its own (ADR-079 Rule 14), per page load: an SPA
 * navigation away from a suppressed route does not lift the replay
 * quarantine, a reload does.
 */
export function createSuppressedPathBeforeSend(patterns: readonly RegExp[]): BeforeSend {
  const stage = suppressedPathStage(stable(patterns), { quarantined: false });
  return (event) => {
    if (event === null) return null;
    return stage(event) ? null : event;
  };
}

// ─── Stage: autocapture from a `ph-no-capture` subtree ────────────────────────

const AUTOCAPTURE_FAMILY = new Set(['$autocapture', '$rageclick', '$dead_click']);

/**
 * What an excluded page sends none of: the autocapture family, and web
 * vitals, whose layout-shift score moves when an element appears late.
 */
const PAGE_EXCLUDED_EVENTS = new Set([...AUTOCAPTURE_FAMILY, '$web_vitals']);

const WEB_VITALS_METRIC = /^\$web_vitals_.+_event$/;

/**
 * The page each metric of a `$web_vitals` event was measured on. posthog-js
 * buffers metrics with their own page URL and flushes them later — on a timer,
 * or when the next page's first metric arrives — under whatever page is
 * current by then, so the event's own URL may name a different page. A metric
 * whose page cannot be read is `undefined`.
 */
function webVitalsPages(properties: Record<string, unknown>): (string | undefined)[] {
  return Object.entries(properties)
    .filter(([key]) => WEB_VITALS_METRIC.test(key))
    .map(([, metric]) => (isRecord(metric) ? pathnameOf(metric.$current_url) : undefined));
}

/** `ph-no-capture` as a whole class token in a serialised element chain. */
const NO_CAPTURE_IN_CHAIN = /(^|[.\s";:])ph-no-capture(?=$|[.\s";:])/;

function elementHasNoCapture(element: unknown): boolean {
  if (!isRecord(element)) return false;
  const classes = element.classes ?? element.attr__class;
  if (Array.isArray(classes)) return classes.includes('ph-no-capture');
  if (typeof classes === 'string') return classes.split(/\s+/).includes('ph-no-capture');
  return false;
}

/**
 * Whether an autocapture-family event came from inside a `ph-no-capture`
 * subtree. The SDK honours the class for most of these, but not on every path
 * (heatmap-driven dead clicks, for one), and the class means "never capture
 * this subtree" wherever it is used. A chain of unexpected shape fails closed.
 */
function fromUncapturedSubtree(event: BeforeSendEvent): boolean {
  if (!AUTOCAPTURE_FAMILY.has(event.event)) return false;
  const chain = event.properties.$elements_chain;
  const elements = event.properties.$elements;
  if (chain !== undefined && typeof chain !== 'string') return true;
  if (elements !== undefined && !Array.isArray(elements)) return true;
  // No element data at all is what the SDK sends for a dead click inside an
  // uncaptured subtree (it strips the element, not the event): fail closed.
  if (chain === undefined && elements === undefined) return true;
  if (typeof chain === 'string' && NO_CAPTURE_IN_CHAIN.test(chain)) return true;
  return Array.isArray(elements) && elements.some(elementHasNoCapture);
}

// ─── Stage: heatmaps for excluded pages ───────────────────────────────────────

/**
 * Removes heatmap entries recorded on an excluded page. posthog-js buffers
 * heatmap points by page URL and flushes them later, so the entry's key — not
 * the page current at flush time — says where the points were recorded.
 * Returns true when nothing is left to send, or the payload cannot be read.
 */
function heatmapStage(
  matchers: readonly Matcher[],
  fixedPointMatchers: readonly Matcher[],
): (event: BeforeSendEvent) => boolean {
  return (event) => {
    if (event.event !== '$$heatmap') return false;
    if (matchers.length === 0 && fixedPointMatchers.length === 0) return false;
    const data = event.properties.$heatmap_data;
    if (!isRecord(data)) return true;
    for (const [key, points] of Object.entries(data)) {
      const path = pathnameOf(key);
      if (path === undefined || matchers.some((m) => m.test(path))) {
        delete data[key];
        continue;
      }
      if (!fixedPointMatchers.some((m) => m.test(path))) continue;
      // A click on a fixed-position element (a modal, a pinned panel) lands at
      // the same coordinates for everyone, so its position can name what was
      // clicked. Points that cannot be read as non-fixed are removed too.
      if (!Array.isArray(points)) {
        delete data[key];
        continue;
      }
      const kept = points.filter((point) => isRecord(point) && point.target_fixed === false);
      if (kept.length === 0) delete data[key];
      else data[key] = kept;
    }
    return Object.keys(data).length === 0;
  };
}

// ─── Stage: visits that start from a sensitive page ───────────────────────────

interface VisitState {
  /** True when the live visit sends nothing. */
  liveSuppressed(): boolean;
  /**
   * Notes the page the browser is on now. `previousPage` is the SDK's own
   * record of the page viewed before this one, which names a visit the hook
   * may never have seen an event for.
   */
  observe(path: string | undefined, previousPage?: string): void;
  /** Whether an event captured on `origin` belongs to a suppressed visit. */
  originSuppressed(origin: string | undefined): boolean;
  /** Exact-path matchers for suppressed visits that are no longer live. */
  scrubMatchers(): Matcher[];
}

function visitState(
  config: EntrySuppressedVisits | undefined,
  schedule: (task: () => void) => void,
): VisitState | undefined {
  if (!config) return undefined;
  const [target] = stable([config.targetPathPattern]);
  const after = stable(config.afterPathPatterns);
  const suppressedPaths = new Map<string, Matcher>();
  let visit: { path: string; suppressed: boolean } | undefined;
  const suppress = (page: string) => {
    if (!suppressedPaths.has(page)) suppressedPaths.set(page, exactPathPattern(page));
  };

  const intentSaysSuppress = (): boolean => {
    if (!config.suppressWhenHistoryState) return false;
    try {
      return config.suppressWhenHistoryState(globalThis.history?.state);
    } catch {
      return true;
    }
  };

  return {
    liveSuppressed: () => visit?.suppressed === true,
    observe(path, previousPage) {
      if (path === undefined) return;
      if (visit?.path === path) {
        // Same page, perhaps a new history entry. An entry that now carries the
        // suppression intent (the resolver replacing an earlier ordinary visit
        // to the same member) suppresses the visit from here on.
        if (!visit.suppressed && intentSaysSuppress()) {
          visit.suppressed = true;
          suppress(path);
        }
        // A same-path navigation pushes an entry with no state: re-mark it
        // while the visit is suppressed, so a reload of THIS entry is still
        // recognised.
        const mark = config.onSuppressedVisit;
        if (visit.suppressed && mark && !intentSaysSuppress()) schedule(() => mark(path));
        return;
      }
      const previous = visit?.path;
      // A target page in between that sent nothing through this hook, right
      // after a page that makes such a visit sensitive: its references are
      // scrubbed like any suppressed visit's, rather than trusted.
      if (
        target !== undefined &&
        previousPage !== undefined &&
        previousPage !== previous &&
        previousPage !== path &&
        target.test(previousPage) &&
        previous !== undefined &&
        after.some((pattern) => pattern.test(previous))
      ) {
        suppress(previousPage);
      }
      // A target page reached from a suppressed one stays suppressed: the
      // host may move between pages for the same subject (an alternate
      // identifier, say) without carrying any marker, and a visit that resumed
      // right after the sensitive page would answer the question it closed.
      const suppressed =
        target !== undefined &&
        target.test(path) &&
        ((previous !== undefined && after.some((pattern) => pattern.test(previous))) ||
          visit?.suppressed === true ||
          intentSaysSuppress());
      visit = { path, suppressed };
      if (suppressed) {
        suppress(path);
        const mark = config.onSuppressedVisit;
        if (mark) schedule(() => mark(path));
      }
    },
    originSuppressed(origin) {
      if (visit?.suppressed) return true;
      if (origin === undefined || !suppressedPaths.has(origin)) return false;
      // The same page opened again, ordinarily, is its own visit. Only the
      // path is known here, so an event the SDK reports late from the earlier,
      // suppressed visit (a dead click, a web-vitals flush, a heatmap) passes
      // this check: a host relies on page-wide exclusions to drop those event
      // classes on its target page.
      return !(visit?.path === origin && !visit.suppressed);
    },
    scrubMatchers() {
      return [...suppressedPaths.entries()]
        .filter(([path]) => !(visit?.path === path && !visit.suppressed))
        .map(([, matcher]) => matcher);
    },
  };
}

// ─── Replay holds: pause while a sensitive surface can be on screen ───────────

/** Open holds. Module-level, like the navigation listeners: one page, one recorder. */
const replayHolds = new Set<object>();
const replayHoldListeners = new Set<() => void>();

function notifyReplayHolds(): void {
  for (const listener of replayHoldListeners) listener();
}

/**
 * Pauses session replay until the returned release runs, where the provider was
 * built with `replayPausedWhileHeld`. Holds nest; replay resumes once every one
 * is released. Take the hold BEFORE the surface renders and release it after
 * it unmounts: the pause is decided by recording time, so anything recorded
 * between the two is dropped whenever its batch is delivered. A no-op when no
 * provider asked for holds, and never enables replay that is off.
 */
export function holdSessionReplay(): () => void {
  const token = {};
  replayHolds.add(token);
  notifyReplayHolds();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    replayHolds.delete(token);
    notifyReplayHolds();
  };
}

/** Releases every open hold. For tests; production releases its own. */
export function resetReplayHolds(): void {
  replayHolds.clear();
  replayHoldListeners.clear();
}

// ─── Stage: replay paused on some pages, by recording time ────────────────────

interface ReplayPause {
  /** Notes the page the browser is on now, opening or closing a paused interval. */
  observe(path: string | undefined, now: number, eventAt: number): void;
  /** Whether a `$snapshot` batch holds anything recorded while paused. */
  covers(snapshotData: unknown): boolean;
}

/** The first instant after `at` (an open interval must not swallow `at` itself). */
function nextAfter(at: number): number {
  return Number.isFinite(at) ? at + 1 : at;
}

/** How many paused intervals a page load keeps apart; older ones are merged. */
const PAUSE_INTERVAL_LIMIT = 50;

/**
 * Keeps a long session's interval list bounded. The two oldest intervals merge
 * into one spanning both, so the gap between them is treated as paused too:
 * replay recorded there is dropped rather than an old paused interval being
 * forgotten, which would let a late batch from it through.
 */
function mergeOldestWhenFull(intervals: { start: number; end?: number }[]): void {
  if (intervals.length <= PAUSE_INTERVAL_LIMIT) return;
  const [first, second] = intervals;
  if (first === undefined || second === undefined) return;
  intervals.splice(0, 2, { start: first.start, end: second.end });
}

/**
 * Pauses replay while a matching page is current. The decision is by when a
 * batch was RECORDED, not when it arrives: the SDK can drain its compression
 * queue after a stop, so a batch from the paused page may reach this hook
 * after the next page's first event. Every rrweb event in a batch carries its
 * own timestamp; a batch with any event inside a paused interval, or with an
 * event that cannot be placed in time, is dropped whole.
 */
function replayPause(
  patterns: readonly Matcher[],
  whileHeld: boolean,
  controls: ReplayControls,
  schedule: (task: () => void) => void,
  deferResume: (task: () => void) => void,
  now: () => number,
): ReplayPause | undefined {
  if (patterns.length === 0 && !whileHeld) return undefined;
  const intervals: { start: number; end?: number }[] = [];
  const held = () => whileHeld && replayHolds.size > 0;
  // A hold pauses whatever the page: it covers a surface that can be on any.
  const paused = (path: string) => held() || patterns.some((pattern) => pattern.test(path));
  // A resume that has been asked for but not run yet; entering a paused page
  // again before it runs cancels it, and the interval simply stays open.
  let pendingResume: object | undefined;
  // The last moment the hook saw a page that is NOT paused. The SDK can drop
  // the navigation events (its client rate limiter) while letting replay
  // through, so a paused page may first be seen through a replay batch;
  // anything recorded after this moment may already be the paused page.
  let lastSeenUnpaused = Number.NEGATIVE_INFINITY;

  return {
    observe(observedPath, at, eventAt) {
      // An unknown page cannot be judged by path, but a hold pauses anyway.
      const path = observedPath ?? (held() ? '' : undefined);
      if (path === undefined) return;
      const open = intervals.at(-1);
      const isOpen = open !== undefined && open.end === undefined;
      if (paused(path)) {
        pendingResume = undefined;
        if (!isOpen) {
          // Open the interval where the paused page may have begun, not where
          // the hook first noticed it: fail closed for the gap in between.
          intervals.push({ start: Math.min(at, eventAt, nextAfter(lastSeenUnpaused)) });
          mergeOldestWhenFull(intervals);
          schedule(() => controls.stopSessionRecording());
        }
        return;
      }
      if (!isOpen) lastSeenUnpaused = Math.max(lastSeenUnpaused, at);
      if (isOpen && pendingResume === undefined) {
        // The interval closes when the resume RUNS, not when the route
        // changes: until then the page being left may still be on screen, and
        // anything recorded meanwhile belongs to it.
        const token = {};
        pendingResume = token;
        deferResume(() => {
          if (pendingResume !== token) return;
          pendingResume = undefined;
          const live = pathnameOf(currentPageHref());
          if (held() || (live !== undefined && paused(live))) return;
          open.end = now();
          controls.startSessionRecording();
        });
      }
    },
    covers(snapshotData) {
      const items: unknown[] = Array.isArray(snapshotData) ? snapshotData : [snapshotData];
      return items.some((item) => {
        if (!isRecord(item)) return true;
        const at = item.timestamp;
        if (typeof at !== 'number' || !Number.isFinite(at)) return true;
        if (intervals.some(({ start, end }) => at >= start && (end === undefined || at < end))) {
          return true;
        }
        // A meta event names the page it was recorded on.
        const href = isRecord(item.data) ? item.data.href : undefined;
        const path = pathnameOf(href);
        return path !== undefined && paused(path);
      });
    },
  };
}

// ─── Stage: persisted entry attribution naming an excluded page ───────────────

const ENTRY_ATTRIBUTION = /^\$(?:session_entry_|initial_|referrer$)/;

function deleteEntryAttribution(
  record: Record<string, unknown> | undefined,
  matchers: readonly Matcher[],
): void {
  if (!record || matchers.length === 0) return;
  for (const [key, value] of Object.entries(record)) {
    if (!ENTRY_ATTRIBUTION.test(key) || typeof value !== 'string') continue;
    const path = pathnameOf(value);
    if (path === undefined || matchers.some((m) => m.test(path))) delete record[key];
  }
}

// ─── Stage: page geometry of excluded pages ───────────────────────────────────

const PAGE_GEOMETRY = /^\$prev_pageview_(?:last|max)_(?:scroll|content)/;

function deletePageGeometry(
  properties: Record<string, unknown>,
  matchers: readonly Matcher[],
): void {
  if (matchers.length === 0) return;
  const described = pathnameOf(properties.$prev_pageview_pathname);
  if (described === undefined || !matchers.some((m) => m.test(described))) return;
  for (const key of Object.keys(properties)) {
    if (PAGE_GEOMETRY.test(key)) delete properties[key];
  }
}

// ─── Where a delayed event happened ───────────────────────────────────────────

/** How many page entries the log keeps; older ones resolve nothing. */
const PAGE_LOG_LIMIT = 100;

interface PageLog {
  observe(path: string | undefined, at: number): void;
  /** The page that was current at `at`, if the log reaches back that far. */
  pageAt(at: number): string | undefined;
}

/**
 * When each page became current, as this hook saw it. A dead click is reported
 * seconds after the click and posthog-js stamps it with the URL current at
 * DELIVERY, not at the click, while keeping the click's own timestamp and
 * element chain; its page is therefore read from the click time.
 */
function pageLog(): PageLog {
  const entries: { path: string; start: number }[] = [];
  return {
    observe(path, at) {
      if (path === undefined || entries.at(-1)?.path === path) return;
      entries.push({ path, start: at });
      if (entries.length > PAGE_LOG_LIMIT) entries.shift();
    },
    pageAt(at) {
      for (let i = entries.length - 1; i >= 0; i -= 1) {
        const entry = entries[i];
        if (entry && entry.start <= at) return entry.path;
      }
      return undefined;
    },
  };
}

/** The click time of a dead click: its event timestamp, or the SDK's own field. */
function deadClickTime(event: BeforeSendEvent): number | undefined {
  const { timestamp } = event;
  if (timestamp instanceof Date && Number.isFinite(timestamp.getTime())) return timestamp.getTime();
  const own = event.properties.$dead_click_event_timestamp;
  return typeof own === 'number' && Number.isFinite(own) ? own : undefined;
}

// ─── Navigation, independent of analytics delivery ───────────────────────────

/** Listeners notified after every history change; one patch serves them all. */
const navigationListeners = new Set<() => void>();
let navigationPatched = false;

function notifyNavigation(): void {
  for (const listener of navigationListeners) {
    try {
      listener();
    } catch {
      // An analytics fault must never reach the app's navigation.
    }
  }
}

/**
 * Calls `onNavigate` after every history change in this page: `pushState`,
 * `replaceState` and `popstate`. The replay pause and visit tracking must not
 * depend on events reaching the hook — the SDK's client rate limiter can drop
 * every event of a whole visit while still delivering replay — so they watch
 * the history itself, the way the SDK and the router do. The history is
 * patched once per page however many hooks register. A no-op outside a
 * browser.
 */
function observeNavigation(onNavigate: () => void): void {
  const win: unknown = globalThis.window;
  const history: unknown = globalThis.history;
  if (typeof win !== 'object' || win === null || typeof history !== 'object' || history === null) {
    return;
  }
  navigationListeners.add(onNavigate);
  if (navigationPatched) return;
  navigationPatched = true;
  for (const name of ['pushState', 'replaceState'] as const) {
    const original: unknown = Reflect.get(history, name);
    if (typeof original !== 'function') continue;
    Reflect.set(history, name, function patched(this: unknown, ...args: unknown[]) {
      const result: unknown = Reflect.apply(original, this, args);
      notifyNavigation();
      return result;
    });
  }
  const addEventListener: unknown = Reflect.get(win, 'addEventListener');
  if (typeof addEventListener === 'function') {
    Reflect.apply(addEventListener, win, ['popstate', notifyNavigation]);
  }
}

/**
 * Forgets every registered navigation listener (the history patch stays, and
 * notifies nobody). For tests that build many hooks in one page; production
 * builds the provider once.
 */
export function resetNavigationListeners(): void {
  navigationListeners.clear();
}

// ─── The composed hook ────────────────────────────────────────────────────────

/**
 * The web provider's `before_send`, in order (the first stage that drops wins):
 *
 * 1. Replay-pause state updates first, so a cold or suppressed entry onto a
 *    paused page still stops recording; a batch recorded while paused drops.
 * 2. Suppressed visits: an event whose origin is a suppressed visit drops —
 *    by its own URL, which outlives a navigation, not the live page. Web
 *    vitals are judged by each metric's own page, for the same reason.
 * 3. Suppressed paths (unchanged behaviour), with their replay quarantine.
 * 4. Autocapture from a `ph-no-capture` subtree drops.
 * 5. Heatmap entries for excluded pages are removed.
 * 6. References to a past suppressed visit are scrubbed from events that
 *    legitimately originate elsewhere.
 * 7. Autocapture-family events lose element text, attributes and link
 *    targets, keeping tag, classes and position (`maskAutocaptureContent`).
 * 8. URL-bearing properties are rewritten to route templates
 *    (`urlPageTemplates`). Last, so every stage above matched raw paths.
 */
export function createPostHogBeforeSend(
  options: PostHogPrivacyOptions = {},
  controls: ReplayControls = INERT_REPLAY_CONTROLS,
  runtime: BeforeSendRuntime = {},
): BeforeSend {
  const now = runtime.now ?? (() => Date.now());
  const schedule = runtime.schedule ?? ((task: () => void) => queueMicrotask(task));
  const quarantine: ReplayQuarantine = { quarantined: false };
  const suppressedMatchers = stable(options.suppressedPathPatterns);
  const suppressedPaths = suppressedPathStage(suppressedMatchers, quarantine);
  const heatmaps = heatmapStage(
    stable(options.heatmapExcludedPathPatterns),
    stable(options.heatmapFixedPointExcludedPathPatterns),
  );
  const visits = visitState(options.entrySuppressedVisits, schedule);
  const attributionExcluded = stable(options.entryAttributionExcludedPathPatterns);
  const autocaptureExcluded = stable(options.autocaptureExcludedPathPatterns);
  const pages = pageLog();
  const geometryExcluded = stable(options.pageGeometryExcludedPathPatterns);
  const previousOn = stable(options.previousPageviewExcluded?.onPathPatterns);
  const previousNaming = stable(options.previousPageviewExcluded?.namingPathPatterns);
  const deferResume =
    runtime.deferResume ?? ((task: () => void) => void setTimeout(task, RESUME_DELAY_MS));
  const pause = replayPause(
    stable(options.replayPausedPathPatterns),
    options.replayPausedWhileHeld === true,
    controls,
    schedule,
    deferResume,
    now,
  );
  if (pause && options.replayPausedWhileHeld === true) {
    // A hold opening or closing is a pause boundary like a navigation, and it
    // may come with no event or navigation at all for the hook to notice.
    replayHoldListeners.add(() => {
      const at = now();
      pause.observe(pathnameOf(currentPageHref()), at, at);
    });
  }
  if (pause || visits || autocaptureExcluded.length > 0) {
    // Visits, the dead-click page log and the replay pause follow navigation
    // itself, not only events that reach the hook: the SDK's rate limiter can
    // drop every event of a visit.
    observeNavigation(() => {
      const at = now();
      const path = pathnameOf(currentPageHref());
      pages.observe(path, at);
      visits?.observe(path);
      // A suppressed visit no event reached still quarantines replay.
      if (visits?.liveSuppressed()) quarantine.quarantined = true;
      pause?.observe(path, at, at);
    });
  }
  /** Whether a page an event names must send nothing; an unknown page fails closed. */
  const pageWithheld = (page: string | undefined): boolean =>
    page === undefined ||
    visits?.originSuppressed(page) === true ||
    suppressedMatchers.some((m) => m.test(page)) ||
    autocaptureExcluded.some((m) => m.test(page));
  const pageScoped =
    visits !== undefined || suppressedMatchers.length > 0 || autocaptureExcluded.length > 0;
  const maskContent = options.maskAutocaptureContent === true;
  const templateUrls = createUrlTemplateStage(options.urlPageTemplates);

  return (event) => {
    if (event === null) return null;
    const at = now();
    const live = livePath(event.properties);
    pages.observe(live, at);
    // Where the event happened. For a dead click that is the page current at
    // the CLICK; one whose page cannot be established is not sent.
    let origin = originPath(event.properties);
    if (event.event === '$dead_click') {
      const clickedAt = deadClickTime(event);
      origin = clickedAt === undefined ? undefined : pages.pageAt(clickedAt);
      if (origin === undefined) return null;
    }

    pause?.observe(live, at, eventTime(event, at));
    if (event.event === '$snapshot' && pause?.covers(event.properties.$snapshot_data)) {
      return null;
    }

    if (visits) {
      visits.observe(
        live,
        pathnameOf(event.properties.$prev_pageview_pathname) ??
          pathnameOf(event.properties.$prev_pageview_url),
      );
      if (visits.originSuppressed(origin)) {
        quarantine.quarantined = true;
        return null;
      }
      if (visits.liveSuppressed()) quarantine.quarantined = true;
    }

    // Web vitals are judged by the page each metric was measured on, not the
    // page current when the buffer flushed.
    if (event.event === '$web_vitals' && pageScoped) {
      if (webVitalsPages(event.properties).some(pageWithheld)) return null;
    }

    // Any stage may have quarantined replay for this page load.
    if (event.event === '$snapshot' && quarantine.quarantined) return null;
    if (suppressedPaths(event)) return null;
    if (event.event === '$snapshot') return event;
    if (fromUncapturedSubtree(event)) return null;
    if (PAGE_EXCLUDED_EVENTS.has(event.event) && autocaptureExcluded.length > 0) {
      // The event's own URL: a dead click is reported after a timeout, by
      // which time the browser may be on another page.
      const where = origin ?? live;
      if (where === undefined || autocaptureExcluded.some((m) => m.test(where))) return null;
    }
    if (heatmaps(event)) return null;

    deletePageGeometry(event.properties, geometryExcluded);
    // On some pages, the page viewed before is itself the disclosure (a member,
    // right before the escalation queue): the whole record goes.
    if (previousOn.length > 0) {
      const here = origin ?? live;
      const before = pathnameOf(event.properties.$prev_pageview_pathname);
      if (
        here !== undefined &&
        before !== undefined &&
        previousOn.some((m) => m.test(here)) &&
        previousNaming.some((m) => m.test(before))
      ) {
        for (const key of Object.keys(event.properties)) {
          if (key.startsWith('$prev_pageview_')) delete event.properties[key];
        }
      }
    }
    deleteEntryAttribution(event.properties, attributionExcluded);
    deleteEntryAttribution(event.$set, attributionExcluded);
    deleteEntryAttribution(event.$set_once, attributionExcluded);

    if (visits) {
      const matchers = visits.scrubMatchers();
      // The previous-pageview context is one record: when its path is removed,
      // its duration and scroll figures would still say a page was viewed.
      const { properties } = event;
      const previousPageview = Object.keys(properties).filter((key) =>
        key.startsWith('$prev_pageview_'),
      );
      if (previousPageview.some((key) => containsSuppressedPath(properties[key], matchers))) {
        for (const key of previousPageview) delete properties[key];
      }
      deleteSuppressedKeys(event.properties, matchers);
      deleteSuppressedKeys(event.$set, matchers);
      deleteSuppressedKeys(event.$set_once, matchers);
    }
    if (maskContent && !maskAutocaptureContent(event)) return null;
    if (templateUrls && !templateUrls(event)) return null;
    return event;
  };
}
